import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Avatar,
  ButtonBase,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import MicIcon from '@material-ui/icons/Mic';
import SendIcon from '@material-ui/icons/Send';
import SettingsVoiceIcon from '@material-ui/icons/SettingsVoice';
import { useConfirm } from 'material-ui-confirm';

import useAudioRecorder from 'hooks/useAudioRecorder';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useAppDispatch } from 'store';
import { FeatureFlagsEnum } from 'store/reducers/atendimentos';
import { setGravandoAudio } from 'store/reducers/mensagens';

type AudioRecorderProps = {
  onEnded?: (mediaBlob: Blob, duration: number) => void;
};

const MAX_DURATION = 10; // tempo maximo de gravação em minutos

const useStyles = makeStyles(({ palette, spacing }) => ({
  popover: {
    minWidth: spacing(40),
  },
  avatarBackground: {
    color: palette.error.main,
    backgroundColor: alpha(palette.error.main, 0.1),
  },
  icon: {
    transform: 'translate(0, 0)',
    animation: '$shake 1.2s infinite',
  },
  '@keyframes shake': {
    '0%': { transform: 'scale(1.25, 0.75)' },
    '50%': { transform: 'scale(1, 1)' },
    '55%': { transform: 'rotate(15deg)' },
    '60%': { transform: 'rotate(-15deg)' },
    '65%': { transform: 'rotate(15deg)' },
    '70%': { transform: 'rotate(-15deg)' },
    '100%': { transform: 'scale(1.25, 0.75)' },
  },
}));

export default function AudioRecorder({ onEnded }: AudioRecorderProps) {
  const { hasFeature } = useFeatureFlags();
  const dispatch = useAppDispatch();

  const deveUsarNovaLibDeAudio = useMemo(
    () => hasFeature(FeatureFlagsEnum.NEW_AUDIO_SENDER_LIB),
    [hasFeature],
  );

  const classes = useStyles();
  const confirm = useConfirm();
  const [ended, setEnded] = useState(false);
  const [duracao, setDuracao] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const {
    startRecording,
    stopRecording,
    recording,
    minutes,
    seconds,
    error,
    duration,
    mediaBlob,
  } = useAudioRecorder({
    deveUsarNovaLibDeAudio,
  });

  const handleStartRecord = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      startRecording();
    },
    [startRecording],
  );

  const handleStopRecord = useCallback(() => {
    setAnchorEl(null);
    stopRecording();
  }, [stopRecording]);

  const handleEnded = () => {
    // Só possibilita o envio se o audio tiver pelo menos 1 segundo de duração
    if (duration >= 1) {
      setDuracao(duration);
      stopRecording();
      setEnded(true);
    }
  };

  // encerra a gravacao automaticamente se atingir o tempo limite de minutos
  useEffect(() => {
    if (minutes >= MAX_DURATION) handleStopRecord();
  }, [minutes, handleStopRecord]);

  useEffect(() => {
    if (recording) {
      dispatch(setGravandoAudio(true));
    }

    return () => {
      dispatch(setGravandoAudio(false));
    };
  }, [recording, dispatch]);

  // display error message
  useEffect(() => {
    if (error) {
      confirm({
        title: <Typography variant="body2">{error.title}</Typography>,
        description: (
          <Typography variant="caption">{error.description}</Typography>
        ),
        dialogProps: { maxWidth: 'xs' },
        cancellationButtonProps: { style: { display: 'none' } },
      }).catch(() => {
        //
      });
    }
  }, [confirm, error]);

  // envio de audio
  useEffect(() => {
    // verifica se ja tem o arquivo Blob de audio pronto e se a ação é parar enviar o audio
    if (mediaBlob && ended) {
      if (onEnded) onEnded(mediaBlob, duracao);

      setEnded(false);
      setDuracao(0);
    }
  }, [duracao, ended, mediaBlob, onEnded]);

  const open = Boolean(anchorEl) && recording;
  const id = open ? 'audio-recorder-popover' : undefined;

  return (
    <>
      <ButtonBase
        title="Gravar mensagem de voz"
        disableRipple
        aria-describedby={id}
        onClick={handleStartRecord}
        disabled={recording}
      >
        {deveUsarNovaLibDeAudio ? (
          <SettingsVoiceIcon color={recording ? 'disabled' : 'inherit'} />
        ) : (
          <MicIcon color={recording ? 'disabled' : 'inherit'} />
        )}
      </ButtonBase>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleStopRecord}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        classes={{ paper: classes.popover }}
      >
        <ListItem>
          <ListItemText
            primary="Gravando áudio..."
            primaryTypographyProps={{
              color: 'textSecondary',
              variant: 'body2',
            }}
          />
        </ListItem>

        <ListItem>
          <ListItemAvatar>
            <Avatar className={classes.avatarBackground}>
              <MicIcon className={classes.icon} />
            </Avatar>
          </ListItemAvatar>

          <ListItemText
            primary={`${minutes}:${seconds > 9 ? '' : '0'}${seconds}`}
            primaryTypographyProps={{ variant: 'body2' }}
          />

          <IconButton color="secondary" onClick={handleStopRecord}>
            <DeleteIcon />
          </IconButton>

          <IconButton edge="end" color="primary" onClick={handleEnded}>
            <SendIcon />
          </IconButton>
        </ListItem>
      </Popover>
    </>
  );
}
